import React, { useEffect, useState } from "react";
import { Container, Grid, Button, Box, Alert } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  TABLE_HEAD_FUND_REBALANCE_LOCATES,
  findFundAbbById,
  hashAction,
  hashPosition,
} from "../../../../../../../component/Methods";
import Locates from "./components/Locates";
import LocatesNotFoundPopup from "../../../../../Popup/LocatesNotFoundPopup";
import { useDispatch, useSelector } from "react-redux";
import {
  
  selectActiveRequests,
} from "../../../../../../../Redux/Reducers/selectors";
import { stageTwoOfRebalance } from "../../../../../../../Redux/Reducers/rebalancerSlice";
import Step2Skeleton from "../../../../../../../component/spinner/Step2Skeleton";
import { useRebalancerState } from "../../../../../../../hooks/useOptimizedState";

const FundRebalanceStep2 = ({ setCurrentStep, onPrevious }) => {
  const dispatch = useDispatch();
  const activeRequests = useSelector(selectActiveRequests);
  const { AllTrades, custodians, funds } = useRebalancerState();

  const [popupOpen, setPopupOpen] = useState(false);
  const [locatesData, setLocatesData] = useState({});

  const isLoadingLocates = activeRequests.includes("rebalancer_locates");

  const groupByCustodian = (orders, funds) => {
    const updatedOrders = orders?.map((order) => {
      const matchedFund = funds?.find(
        (fund) => fund?.abbreviation === order?.fund
      );
      if (matchedFund && matchedFund?.accounts) {
        const account = matchedFund?.accounts[order?.custodian];
        if (account) {
          return { ...order, custodianId: account?.custodian };
        }
      }
      return { ...order, custodianId: "N/A" };
    });

    return updatedOrders?.reduce((grouped, trade) => {
      if (!grouped[trade?.custodianId]) {
        grouped[trade?.custodianId] = [];
      }
      grouped[trade?.custodianId].push(trade);
      return grouped;
    }, {});
  };

  useEffect(() => {
    if (AllTrades && Array.isArray(AllTrades?.realOrders)) {
      const TickerTrades = AllTrades?.realOrders;
      if (TickerTrades?.length > 0) {
        const filteredTrades = TickerTrades.filter((trade) => {
          const tradeType =
            hashAction(trade.action) + hashPosition(trade.position);
          return tradeType.toLowerCase() == "sellshort";
        });

        if (filteredTrades?.length > 0) {
          const updatedData = filteredTrades.map((trade, index) => {
            const residual =
              trade?.residual || trade?.residual === 0
                ? trade?.residual
                : trade?.shareCount;
            return {
              ticker: trade?.ticker?.symbol?.toUpperCase() || "",
              shareCount: trade.shareCount,
              residual: residual,
              available:
                trade?.availableLocates || trade?.availableLocates === 0
                  ? trade.availableLocates
                  : residual,
              custodian: trade.custodian,
              fund: findFundAbbById(trade?.fund, funds),
              id: trade?.tradeId,
              key: index,
            };
          });

          const groupedData = groupByCustodian(updatedData, funds);
          setLocatesData(groupedData);
        }
      }
    }
  }, [AllTrades]);

  const groupedOrders = locatesData;

  const handleLocatesDataChange = (updatedData, custodian) => {
    setLocatesData((prevData) => {
      const updatedGroupedOrders = { ...prevData };
      updatedGroupedOrders[custodian] = updatedData;
      return updatedGroupedOrders;
    });
  };

  const updateAvailableLocates = (realOrders, locatesData) => {
    const allLocates = Object.values(locatesData).flat();

    return realOrders.map((trade) => {
      const locatesMatch = allLocates?.find(
        (locate) =>
          locate.ticker.toLowerCase() === trade.ticker.symbol.toLowerCase() &&
          locate.id === trade.tradeId &&
          locate.shareCount === trade.shareCount
      );

      if (locatesMatch) {
        return {
          ...trade,
          availableLocates: parseFloat(locatesMatch.available),
        };
      }

      return trade;
    });
  };

  const onNext = () => {
    if (locatesData) {
      const updatedTickerTrades = updateAvailableLocates(
        AllTrades.realOrders,
        locatesData
      );

      const allTrades = {
        ...AllTrades,
        realOrders: updatedTickerTrades,
      };
      dispatch(stageTwoOfRebalance(allTrades));
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleFindLocatesClick = () => {
    setPopupOpen(true);
  };
  const handleClosePopup = () => {
    setPopupOpen(false);
  };
  return (
    <Container disableGutters maxWidth="100%" sx={{ marginTop: 2 }}>
      {isLoadingLocates ? (
        <Step2Skeleton />
      ) : (
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Alert severity="info">
                Locates Available column is editable, enter the numbers or use ⋮
                icon to auto fill from API.
              </Alert>

              {/* {Object.keys(locatesData).length > 0 && ( */}
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={onNext}
                  sx={{ width: "150px", height: "40px" }}
                >
                  Next
                </Button>
              </Box>
              {/* )} */}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                flexWrap: "nowrap",
                gap: 2,
              }}
            >
              {Object.keys(groupedOrders).map((custodian) => (
                <Box key={custodian} sx={{ width: "400px" }}>
                  <Locates
                    tickerTrades={groupedOrders[custodian]}
                    setLocatesData={handleLocatesDataChange}
                    tableHeader={TABLE_HEAD_FUND_REBALANCE_LOCATES}
                    custodian={custodian}
                    custodians={custodians}
                  />
                </Box>
              ))}
            </Box>
          </Grid>

          {Object.keys(locatesData).length === 0 && (
            <Grid item xs={12}>
              <p>No data available</p>
            </Grid>
          )}
        </Grid>
      )}

      <LocatesNotFoundPopup open={popupOpen} onClose={handleClosePopup} />
    </Container>
  );
};

export default FundRebalanceStep2;
